'use client'

import { useEffect, useRef, useState } from 'react'

type TimeRemaining = {
  total: number
  days: number
  hours: number
  minutes: number
  seconds: number
}

const getTimeRemaining = (launchAt: string): TimeRemaining | null => {
  const target = new Date(launchAt).getTime()
  if (!Number.isFinite(target)) return null

  const total = target - Date.now()
  const bounded = Math.max(total, 0)

  return {
    total,
    days: Math.floor(bounded / (1000 * 60 * 60 * 24)),
    hours: Math.floor((bounded / (1000 * 60 * 60)) % 24),
    minutes: Math.floor((bounded / (1000 * 60)) % 60),
    seconds: Math.floor((bounded / 1000) % 60),
  }
}

const padTime = (value: number) => value.toString().padStart(2, '0')

export function ProductLaunchCountdown({
  launchAt,
}: ProductLaunchCountdownProps) {
  const [remaining, setRemaining] = useState<TimeRemaining | null>(() =>
    launchAt ? getTimeRemaining(launchAt) : null
  )
  const hasRefreshedRef = useRef(false)
  const hadPositiveTimeRef = useRef(false)

  useEffect(() => {
    if (!launchAt) return

    hasRefreshedRef.current = false
    hadPositiveTimeRef.current = false

    const maybeRefresh = (next: TimeRemaining | null) => {
      if (!next || hasRefreshedRef.current) return
      if (next.total > 0) {
        hadPositiveTimeRef.current = true
        return
      }
      if (!hadPositiveTimeRef.current) return

      hasRefreshedRef.current = true
      window.location.reload()
    }

    const tick = () => {
      const next = getTimeRemaining(launchAt)
      setRemaining(next)
      maybeRefresh(next)
    }

    tick()
    const interval = window.setInterval(tick, 1000)

    return () => window.clearInterval(interval)
  }, [launchAt])

  if (!launchAt || !remaining) {
    return (
      <div className="rounded-lg border border-white/25 bg-white/10 p-5 text-white shadow-xl backdrop-blur">
        <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
          Launch date
        </p>
        <p className="mt-2 text-2xl font-bold">Coming soon</p>
      </div>
    )
  }

  const units = [
    { label: 'Days', value: remaining.days },
    { label: 'Hours', value: remaining.hours },
    { label: 'Minutes', value: remaining.minutes },
    { label: 'Seconds', value: remaining.seconds },
  ]

  return (
    <div className="rounded-lg border border-white/25 bg-white/10 p-4 text-white shadow-xl backdrop-blur md:p-5">
      <p className="text-sm font-semibold uppercase tracking-[0.2em] text-white/70">
        Launches in
      </p>
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
        {units.map(unit => (
          <div
            key={unit.label}
            className="rounded-md border border-white/15 bg-black/35 px-3 py-4 text-center"
          >
            <div className="font-archivo text-3xl font-black leading-none sm:text-4xl">
              {unit.label === 'Days' ? unit.value : padTime(unit.value)}
            </div>
            <div className="mt-2 text-xs font-semibold uppercase tracking-[0.18em] text-white/65">
              {unit.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

interface ProductLaunchCountdownProps {
  launchAt: string | null
}
